"use client"
import { SectionHeadingTitle } from "@/components/SectionTitle";
import BookmarkSquareIcon from "@heroicons/react/24/solid/BookmarkSquareIcon";
import GuestCommentForm from "./GuestCommentForm";
import CommentCard from "@/components/views/venue-details/card/comments/CommentCard";
import { useState } from "react";
import dynamic from "next/dynamic";
import toast from "react-hot-toast";
import { useTranslation } from "@/hooks/translationContext";
import capitalizeWords from "@/lib/caplitalize-words";
import { useSession } from "next-auth/react";

const AddReplyModal = dynamic(() => import("@/components/views/venue-details/new-details/comments/AddReplyModal"))

export default function BlogDetailComments({
  blog,
  data,
}: {
  blog: any;
  data: any;
}) {
  const [parentComment, setParentComment] = useState({} as ParentComment);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {t} = useTranslation();
  const { status: sessionStatus } = useSession();

  const handleShowReply = (parent) => {
    setParentComment(parent);
    setShowReplyModal(true);
  };

  const handleSubmit = async (data): Promise<boolean | number> => {
    const toastId = toast.loading(t("venue-detail-reply-submit-loading"));
    setIsLoading(true);

    try {
      const body = {
        fullName: data.fullName,
        blogId: blog.id.toString(),
        comment: data.text,
        rating: data.rating || 5,
        parent_id: data.parent_id.toString(),
        ...(data.phone && { phone: data.phone }),
        ...(data.instagram_username && { instagram_username: data.instagram_username }),
        ...(data.address && { address: data.address }),
        ...(data.email && { email: data.email }),
      }

      const { createBlogCommentFormAction } = await import("@/actions/blog/blog");
      const response = await createBlogCommentFormAction(body);

      if (response?.success) {
        toast.success(t("venue-detail-reply-pending-approval-info"), {
          id: toastId,
        });
        setShowReplyModal(false);
        setParentComment(null);
        return true;
      } else {
        toast.error(t("venue-detail-reply-submit-error"), { id: toastId });
        return false;
      }
    } catch (error) {
      console.log("error", error)
      toast.error(t("venue-detail-reply-submit-error"), { id: toastId });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Transform blog comment data to venue comment format
  const transformComment = (comment): any => {
    return {
      id: comment.id,
      text: comment.text,
      userName: comment.user ? `${comment.user?.firstname} ${comment.user?.lastname}` : comment.fullName || "Anonim",
      userId: comment.user?.id,
      avatar: comment.user?.avatar,
      rating: comment.rating || 0,
      approvedAt: comment.approved_at || comment.approvedAt,
      repliesCount: comment.replies?.length || 0,
      replies: comment.replies ? comment.replies.map(transformComment) : [],
    };
  };

  const sectionTitle = t("blog-commentSection-title", {defaultValue: "Yorum Yap/Soru Sor", blog: capitalizeWords(blog?.descriptions?.[0]?.title || "") })

  return (
    <div className="flex flex-col gap-4">
      <SectionHeadingTitle title={sectionTitle} icon={null} showRedBar />

      <GuestCommentForm
        blogId={blog.id}
      />

      {Array.isArray(data?.comments) && data?.comments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2">
          {data?.comments.map((comment) => (
            <CommentCard
              key={comment.id}
              handleShowReply={handleShowReply}
              comment={transformComment(comment)}
            />
          ))}
        </div>
      )}

      {parentComment && (
        <AddReplyModal
          isLoading={isLoading}
          disableRating
          handleSubmit={handleSubmit}
          parentComment={parentComment}
          isOpen={showReplyModal}
          onClose={() => setShowReplyModal(false)}
        />
      )}
    </div>
  );
}
